import Cookies from "js-cookie"
import i18n from "./i18n"
import { CustomConfig } from "./models/CustomConfig"
import { FeatureService } from "./services/FeatureService"
import { toast } from "react-toastify"

declare let jQuery: any

export function persistCurrentLanguage(language: string) {
    // try using localstorage if possible
    try {
        localStorage.setItem("userLanguage", language)
    } catch {
        Cookies.set("userLanguage", language, { expires: 7 })
    }
}

export function getCurrentLanguage() {
    if (!FeatureService.isFeatureActive("CHANGE_LANGUAGE")) {
        persistCurrentLanguage("de")
        return "de"
    }

    // try using localstorage if possible
    let language = localStorageOrCookieLanguage()
    if (!language) {
        language = window.navigator.language
    }

    if (!language) {
        language = "de"
    }
    if (language) language = language.substr(0, 2)
    persistCurrentLanguage(language)

    return language
}

export function localStorageOrCookieLanguage() {
    let language = localStorage.getItem("userLanguage") || undefined
    if (!language) {
        language = Cookies.get("userLanguage")
    }

    return language
}

export const b64toBlob = (b64Data: string, contentType = "", sliceSize = 512) => {
    const byteCharacters = atob(b64Data)
    const byteArrays = []

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize)

        const byteNumbers = new Array(slice.length)
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i)
        }

        byteArrays.push(new Uint8Array(byteNumbers))
    }

    return new Blob(byteArrays, { type: contentType })
}

export const contains = (selector: string, text: RegExp | string) => {
    const elements = document.querySelectorAll(selector)
    return Array.from(
        new Set(
            Array.prototype.filter.call(elements, function (element) {
                return RegExp(text).test(element.textContent)
            }),
        ),
    )
}

export const groupBy = (key: string | number) => (array: any[]) =>
    array.reduce((objectsByKeyValue, obj) => {
        const value = obj[key]
        objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj)
        return objectsByKeyValue
    }, {})

export const addValidationTextToInput = (code: string, text: string, dangerClass: string, invalidClass: string, position?: string) => {
    const cont = contains("label", `\\(${code?.toLocaleUpperCase()}\\)`)
    cont.forEach((label) => {
        const errorElement = document.createElement("small")
        errorElement.classList.add(dangerClass)
        errorElement.innerText = i18n.t(text) || ""

        const inputElement = document.getElementById(label?.htmlFor)
        if (position) {
            if (inputElement?.closest("." + position)) {
                inputElement?.classList.add(invalidClass)

                const colDiv = label?.nextElementSibling
                colDiv?.insertBefore(errorElement, colDiv.nextElementSibling)
                return
            }
        } else {
            inputElement?.classList.add(invalidClass)

            const colDiv = label?.nextElementSibling
            colDiv?.insertBefore(errorElement, colDiv.nextElementSibling)
        }

        // TODO this causes issues with react state (need to click twice to collapse again) - keep anyway?
        // try to un-collapse all parents of the input element
        let parent = inputElement?.parentElement
        while (parent) {
            if (parent.classList.contains("collapse") && !parent.classList.contains("show")) {
                jQuery(parent).collapse(false)
            }
            parent = parent.parentElement
        }
    })
}

export const download = (blob: Blob, filename: string, target?: string) => {
    // Create an object URL for the blob object
    const url = URL.createObjectURL(blob)

    // Create a new anchor element
    const a = document.createElement("a")

    // Set the href and download attributes for the anchor element
    // You can optionally set other attributes like `title`, etc
    // Especially, if the anchor element will be attached to the DOM
    a.href = url
    a.download = filename || "download"
    a.target = target || ""
    a.rel = target ? "noopener noreferrer" : ""

    // Click handler that releases the object URL after the element has been clicked
    // This is required for one-off downloads of the blob content
    const clickHandler = () => {
        setTimeout(() => {
            URL.revokeObjectURL(url)
            a.removeEventListener("click", clickHandler)
        }, 150)
    }

    // Add the click event listener on the anchor element
    // Comment out this line if you don't want a one-off download of the blob content
    a.addEventListener("click", clickHandler, false)

    // Programmatically trigger a click on the anchor element
    // Useful if you want the download to happen automatically
    // Without attaching the anchor element to the DOM
    // Comment out this line if you don't want an automatic download of the blob content
    a.click()

    // Return the anchor element
    // Useful if you want a reference to the element
    // in order to attach it to the DOM or use it in some other way
    return a
}

export const sortingAlerts = () => {
    jQuery(function () {
        const elem = jQuery("#validierungContainer > div")
            .toArray()
            .sort(function (a: any, b: any) {
                const aa = jQuery(a).data("sort")
                const bb = jQuery(b).data("sort")

                return aa < bb ? -1 : aa > bb ? 1 : 0
            })
        jQuery(elem).appendTo("#validierungContainer")
    })
}

export const handleLanguageChange = () => {
    jQuery(document).trigger("_page_ready")
}

export let rechnungFormats: string[]

export const handleFormats = (config: CustomConfig | undefined): CustomConfig | undefined => {
    if (!config) {
        return config
    }

    rechnungFormats = process.env.NODE_ENV === "development" ? ["UBL", "CII", "SCA"] : config.rechnungFormats || ["UBL", "CII"]

    return config
}

export let uploadAdvices: string[] = ["general.dateiUpload.uploadHinweise.eins"]
export const hanldeCustomUploadAdvices = (config: CustomConfig | undefined): CustomConfig | undefined => {
    if (!config) {
        return config
    }

    if (config.customUploadAdvices && config.customUploadAdvices.length > 0) {
        uploadAdvices = config.customUploadAdvices.concat(uploadAdvices)
    }

    return config
}

export function get<T, P1 extends keyof NonNullable<T>>(obj: T, prop1: P1): NonNullable<T>[P1] | undefined

export function get<T, P1 extends keyof NonNullable<T>, P2 extends keyof NonNullable<NonNullable<T>[P1]>>(
    obj: T,
    prop1: P1,
    prop2: P2,
): NonNullable<NonNullable<T>[P1]>[P2] | undefined

export function get<
    T,
    P1 extends keyof NonNullable<T>,
    P2 extends keyof NonNullable<NonNullable<T>[P1]>,
    P3 extends keyof NonNullable<NonNullable<NonNullable<T>[P1]>[P2]>,
>(obj: T, prop1: P1, prop2: P2, prop3: P3): NonNullable<NonNullable<NonNullable<T>[P1]>[P2]>[P3] | undefined

export function get(obj: any, ...props: string[]): any {
    return obj && props.reduce((result, prop) => (result == null ? undefined : result[prop]), obj)
}

export const handleErrors = (response: Response) => {
    if (!response.ok) {
        throw Error(response.statusText)
    }
    return response
}

export const handleConnectionError = (e: Error) => {
    console.error("connection error", e)
    toast(i18n.t("general.error.requestFailed", { errorMsg: e.message }), { type: "error" })
}

/**
 * Checks if a value is either null or undefined.
 *
 * Note: we don't need the explicit check for undefined (see the link)
 *
 * @see https://stackoverflow.com/questions/46715994/implementing-isnil
 * @export
 * @param {*} value
 * @returns
 */
export function isNil(value: any) {
    return value == null
}

export const getSteuerberechnungsOption = (steuerberechnung?: string) => {
    // Positionsebene als Default setzen, wenn noch nix angegeben ist oder das Feature aus ist
    if (!FeatureService.isFeatureActive("STEUERBERECHNUNG") || !steuerberechnung || steuerberechnung.trim() == "") {
        steuerberechnung = "P"
    }

    return steuerberechnung
}
